import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { ArrowRight, Bell, CalendarPlus, Check, CreditCard, FileText, Trophy, Users2 } from 'lucide-react';

const FEATURES = [
  {
    icon: <CalendarPlus className='w-7 h-7 text-blue-600' />,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    checkColor: 'text-blue-600',
    title: 'Création de concours',
    desc: "Tête-à-tête, doublette, triplette · Local à National · Dotations personnalisables · Limite d'équipes configurable",
    items: [
      'Brouillon sauvegardé automatiquement',
      'Ouverture programmée des inscriptions',
      'Affiche et page publique dédiée',
    ],
  },
  {
    icon: <Users2 className='w-7 h-7 text-emerald-600' />,
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    checkColor: 'text-emerald-600',
    title: 'Gestion des inscriptions',
    desc: "Inscriptions en ligne 24/7 · Composition d'équipes · Validation organisateur · Gestion des annulations",
    items: ["Formulaire d'inscription en ligne", 'Validation automatique ou manuelle', 'Export CSV des participants'],
  },
  {
    icon: <CreditCard className='w-7 h-7 text-purple-600' />,
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    checkColor: 'text-purple-600',
    title: 'Paiements en ligne',
    desc: 'Stripe sécurisé · Carte bancaire · Suivi en temps réel · Reçus automatiques',
    items: ['Paiement obligatoire ou facultatif', 'Remboursements en quelques clics', 'Comptabilité simplifiée'],
  },
  {
    icon: <Trophy className='w-7 h-7 text-orange-600' />,
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    checkColor: 'text-orange-600',
    title: 'Résultats & podium',
    desc: 'Saisie simplifiée du classement final · Support des ex-æquos · Publication immédiate du podium public',
    items: ['Podium animé et partageable', 'Gestion des demi-finalistes et quarts', 'Historique des résultats public'],
  },
  {
    icon: <Bell className='w-7 h-7 text-pink-600' />,
    iconBg: 'bg-pink-100 dark:bg-pink-900/30',
    checkColor: 'text-pink-600',
    title: 'Notifications automatiques',
    desc: "Emails automatiques · Rappels programmés J-7 et J-1 · Alertes en cas de modification ou d'annulation",
    items: [
      "Confirmation d'inscription instantanée",
      'Rappels automatiques J-7 et J-1 avant le concours',
      'Suivi de paiement par email',
    ],
  },
  {
    icon: <FileText className='w-7 h-7 text-indigo-600' />,
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    checkColor: 'text-indigo-600',
    title: 'Compatible Gestion Concours FFPJP',
    desc: "Catégories d'âge automatiques · Saisie des numéros de licence · Export compatible Gestion Concours FFPJP",
    items: [
      "Calcul automatique des catégories d'âge",
      'Saisie des numéros de licence par les joueurs',
      'Exports au format attendu par la fédération',
    ],
  },
];

export default function FeaturesGrid() {
  return (
    <section id='fonctionnalites' className='px-6 py-32'>
      <div className='max-w-7xl mx-auto'>
        <div className='text-center mb-20'>
          <span className='text-blue-600 font-bold text-sm uppercase tracking-wider'>Fonctionnalités</span>
          <h2 className='text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mt-4 mb-6'>
            Tout ce dont vous avez besoin
            <br />
            <span className='gradient-text'>dans une seule plateforme</span>
          </h2>
          <p className='text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto'>
            BoulesConnect couvre l'intégralité du cycle de vie d'un concours de pétanque, de jeu provençal ou de boule
            lyonnaise — de la création à la remise des prix.
          </p>
        </div>

        <div className='grid md:grid-cols-3 gap-8'>
          {FEATURES.map(feature => (
            <div
              key={feature.title}
              className='bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-200 dark:border-slate-700 hover-lift'
            >
              <div className={`w-14 h-14 ${feature.iconBg} rounded-2xl flex items-center justify-center mb-6`}>
                {feature.icon}
              </div>
              <h3 className='text-xl font-bold text-slate-900 dark:text-white mb-3'>{feature.title}</h3>
              <p className='text-slate-600 dark:text-slate-400 mb-4'>{feature.desc}</p>
              <ul className='space-y-2 text-sm text-slate-500 dark:text-slate-400'>
                {feature.items.map(item => (
                  <li key={item} className='flex items-center gap-2'>
                    <Check className={`w-4 h-4 ${feature.checkColor} flex-shrink-0`} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className='flex justify-center mt-12'>
          <Link
            to={ROUTES.FONCTIONNALITES}
            className='inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-bold border border-blue-200 dark:border-blue-800 px-8 py-3 rounded-2xl hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors'
          >
            Voir toutes les fonctionnalités <ArrowRight className='w-4 h-4' />
          </Link>
        </div>
      </div>
    </section>
  );
}
